import { FC, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { addProductToOrder } from '@store/slices/order';
import { showToast } from '@store/slices/notification';
import { useGetAllProductsQuery, useGetBulkPricingRulesQuery } from '@services';
import { getUnitPrice } from '@helpers';
import { skipToken } from '@reduxjs/toolkit/dist/query';

type Props = {
  productId: number;
  disabled?: boolean;
  selectedStateIndex: number;
};

export const AddToCart: FC<Props> = ({ productId, disabled, selectedStateIndex }) => {
  const [quantity, setQuantity] = useState(1);
  const stateBasedPriceList = useAppSelector((state) => state?.products?.value?.statePriceList);

  const { data: products } = useGetAllProductsQuery();
  const product = products?.find((prod) => prod.id === productId);
  const getStateBasedPrice = () =>
    stateBasedPriceList ? stateBasedPriceList[selectedStateIndex].records.find((item) => item.product_id === productId)?.price : undefined;
  // Initialize a state variable as undefined in order to skip the initial query with a skipToken.
  // The query is only run when queryProductId is set to something other than undefined.
  const [queryProductId] = useState<number | undefined>();
  const { data: bulkPricingRules } = useGetBulkPricingRulesQuery(queryProductId ?? skipToken);

  const productsInCart = useAppSelector((state) => state.order.value.products);

  const dispatch = useAppDispatch();

  const addToCart = () => {
    if (!product) return;

    const productAlreadyInCart = productsInCart?.find((prod) => prod.productId === productId);
    const unitPrice = getUnitPrice(
      quantity + (productAlreadyInCart?.quantity || 0),
      getStateBasedPrice() ?? product.price,
      bulkPricingRules,
    );

    dispatch(
      addProductToOrder({
        productId,
        quantity,
        unitPrice,
        name: product.name,
      }),
    );

    dispatch(
      showToast({
        message: `Added ${quantity}x ${product?.name} to the cart.`,
        type: 'success',
      }),
    );

    setQuantity(1);
  };

  return (
    <Box display="flex">
      <Button data-testid="add-to-cart-button" size="small" variant={'contained'} onClick={addToCart} disabled={disabled}>
        Add
      </Button>
    </Box>
  );
};
